import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { AttributionsIconButton } from '../../components/ui/AttributionsIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  JobPosition,
  JobPositionUpsertPayload,
  createJobPosition,
  deleteJobPosition,
  listJobPositions,
  updateJobPosition,
} from '../../services/jobPositionsApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

type JobPositionFormState = JobPositionUpsertPayload;

const JOB_POSITION_DESCRIPTION_MAX_LENGTH = 2000;

const emptyForm: JobPositionFormState = {
  name: '',
  description: '',
  isActive: true,
};

export function JobPositionsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('people.job-positions');
  const [items, setItems] = useState<JobPosition[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<JobPositionFormState>(emptyForm);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [saving, setSaving] = useState(false);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const allPageSelected =
    items.length > 0 && items.every((item) => selectedIds.has(item.id));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listJobPositions({
        page: targetPage,
        pageSize,
        search: search || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
      setSelectedIds(new Set());
    } catch {
      toast.error('Falha ao carregar cargos.');
    } finally {
      setLoading(false);
    }
  }

  function handleSearch(): void {
    setPage(1);
    void loadData(1);
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(position: JobPosition): void {
    expandForm();
    setEditingId(position.id);
    setForm({
      name: position.name,
      description: position.description,
      isActive: position.isActive,
    });
  }

  function toggleSelect(id: string): void {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleSelectAllOnPage(): void {
    if (allPageSelected) {
      setSelectedIds(new Set());
      return;
    }

    setSelectedIds(new Set(items.map((item) => item.id)));
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    setSaving(true);
    try {
      if (editingId) {
        await updateJobPosition(editingId, form);
        toast.success('Cargo atualizado.');
        collapseForm(resetForm);
      } else {
        await createJobPosition(form);
        toast.success('Cargo criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar cargo.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(position: JobPosition): Promise<void> {
    if (!canWrite) return;

    const accepted = await confirm({
      title: 'Excluir cargo',
      message: `Deseja excluir "${position.name}"?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteJobPosition(position.id);
      toast.success('Cargo excluído.');
      if (editingId === position.id) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir cargo.');
    }
  }

  async function handleDeleteSelected(): Promise<void> {
    if (!canWrite || selectedIds.size === 0) return;

    const accepted = await confirm({
      title: 'Excluir cargos selecionados',
      message: `Deseja excluir ${selectedIds.size} cargo(s) selecionado(s)?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    setDeleting(true);
    try {
      await Promise.all(Array.from(selectedIds).map((id) => deleteJobPosition(id)));
      toast.success(`${selectedIds.size} cargo(s) excluído(s).`);
      if (editingId && selectedIds.has(editingId)) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir cargos selecionados.');
    } finally {
      setDeleting(false);
    }
  }

  const formTitle = editingId ? 'Editar cargo' : 'Novo cargo';
  const editingFormSummary = editingId
    ? `Editando ${form.name || 'cargo selecionado'} — clique para expandir`
    : undefined;

  const searchBar = (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <input
        className="erp-input max-w-md"
        placeholder="Buscar por nome ou descrição..."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleSearch();
          }
        }}
      />
      <button type="button" className="erp-btn-secondary" onClick={handleSearch}>
        Buscar
      </button>
      {canWrite ? (
        <button
          type="button"
          className="erp-btn-secondary text-red-700 disabled:opacity-60"
          disabled={deleting || selectedIds.size === 0}
          onClick={() => void handleDeleteSelected()}
        >
          Excluir selecionados ({selectedIds.size})
        </button>
      ) : null}
    </div>
  );

  return (
    <PageShell>
      <PageHeader
        title="Cargos"
        description="Catálogo de cargos dos colaboradores da empresa."
        actions={(
          <Link to="/people/job-attribution-kinds" className="erp-btn-secondary text-sm">
            Tipos de atribuição
          </Link>
        )}
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={formTitle}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={editingFormSummary}
          createSummary="Clique para expandir e cadastrar um novo cargo"
          isEditing={!!editingId}
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Nome
                <input
                  className="erp-input"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                />
              </label>
            </div>
            <label className="erp-label mt-3 block">
              Descrição
              <textarea
                className="erp-input mt-1 min-h-[60px]"
                value={form.description ?? ''}
                maxLength={JOB_POSITION_DESCRIPTION_MAX_LENGTH}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                placeholder={`Até ${JOB_POSITION_DESCRIPTION_MAX_LENGTH} caracteres`}
              />
              <span className="mt-1 block text-xs text-secondary-500">
                {(form.description ?? '').length} / {JOB_POSITION_DESCRIPTION_MAX_LENGTH} caracteres
              </span>
            </label>
            <label className="erp-label-inline mt-3">
              <input
                type="checkbox"
                checked={form.isActive ?? true}
                onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
              />
              Ativo
            </label>
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      {searchBar}

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
            <table className="erp-table">
              <thead>
                <tr>
                  {canWrite ? (
                    <th className="w-10">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAllOnPage}
                        aria-label="Selecionar página"
                      />
                    </th>
                  ) : null}
                  <th>Nome</th>
                  <th>Status</th>
                  <th className="w-28">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={canWrite ? 4 : 3} className="text-center text-secondary-500">
                      {search ? 'Nenhum cargo encontrado.' : 'Nenhum cargo cadastrado.'}
                    </td>
                  </tr>
                ) : (
                  items.map((position) => (
                    <tr key={position.id}>
                      {canWrite ? (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(position.id)}
                            onChange={() => toggleSelect(position.id)}
                            aria-label={`Selecionar ${position.name}`}
                          />
                        </td>
                      ) : null}
                      <td className="font-medium text-secondary-900">{position.name}</td>
                      <td>
                        <ActiveStatusBadge active={position.isActive} />
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <AttributionsIconButton
                            to={`/people/job-positions/${position.id}/attributions`}
                            label="Editar atribuições do cargo"
                          />
                          {canWrite ? (
                            <>
                              <EditIconButton label="Editar cargo" onClick={() => startEdit(position)} />
                              <DeleteIconButton
                                label="Excluir cargo"
                                onClick={() => void handleDelete(position)}
                              />
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <ListPaginationBar
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          />
        </>
      )}
    </PageShell>
  );
}
